import type { Translation } from "../i18n-types.js"

const de = {
  common: {
    countViews: "{{keine aufrufe|1 aufruf|?? aufrufe}}",
    countReactions: "{{keine reaktionen|1 reaktion|?? reaktionen}}",
    back: "Zurück",
    blog: "Beiträge",
    settings: "Einstellungen",
    addPosts: "beitrag erstellen",
    save: "Speichern",
    filter: "Filter",
    tag: "Tag",
    sort: "Sortieren",
    all: "Alle",
    loading: "Lädt...",
    saving: "Speichern...",
    delete: "Löschen",
    edit: "Bearbeiten",
    close: "Schließen",
    error: "Fehler beim Speichern",
    networkError: "Fehler beim Speichern. Bitte überprüfen Sie Ihre Verbindung",
    validationError: "Bitte überprüfen Sie das Formular",
    by: "von",
    cancel: "Abbrechen",
    private: "Privat",
    holdToConfirm: "Halten zum Bestätigen",
    done: "Fertig!",
    defaultLanguage: "standard",
    translating: "Wird übersetzt...",
    translateAll: "Alle übersetzen",
    translateFrom: "Übersetzen aus {language}",
  },
  mainLayout: {
    errorLoadingData: "Beim Laden der Daten für diese Seite ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    reloadPage: "Seite neu laden",
  },
  dashboard: {
    welcomeBack: "Willkommen zurück",
    latestPosts: "Neueste Beiträge",
    viewAllPosts: "Alle Beiträge anzeigen",
    noPostsYet: "Du hast noch keine Beiträge veröffentlicht.",
    latestDrafts: "Neueste Entwürfe",
    viewAllDrafts: "Alle anzeigen",
    noDrafts: "Du hast keine Entwürfe",
    latestNotes: "Neueste Notizen",
    noNotesYet: "Du hast noch keine Notizen",
    subscribers: "Abonnenten",
    posts: "Beiträge",
    views: "Aufrufe",
    openRate: "Öffnungsrate",
    published: "Veröffentlicht",
    created: "Erstellt",
    recentReactions: "Neueste Reaktionen",
    noReactionsYet: "Du hast noch keine Reaktionen",
    reactedToYourPost: "haben auf deinen Beitrag reagiert",
    othersCount: "{{keine weiteren|1 weitere Person|?? weitere Personen}}",
    createPublication: {
      title: "Erstelle deine eigene Publikation",
      description: "Beginne deine eigene Ecke im Web. teile deine Gedanken, baue deine Leserschaft auf und besitze deine Inhalte.",
      button: "Loslegen",
    },
    verifyEmail: {
      title: "Verifiziere deine E-Mail",
      description: "Verifiziere deine E-Mail-Adresse, um alle Funktionen freizuschalten, wie das Erstellen deiner eigenen Publikation.",
      button: "E-Mail verifizieren",
    },
  },
  settings: {
    title: "Einstellungen",
    localizationTitle: "Lokalisierung",
    localizationDescription: "Wähle deine bevorzugte Sprache",
    interfaceLanguageLabel: "Oberflächensprache",
    authenticationTitle: "Authentifizierung",
    authenticationDescription: "Verwalte dein Konto",
    signedInAs: "Angemeldet als {email}",
    signOut: "Abmelden",
    signingOut: "Abmelden...",
    authorProfileTitle: "Autorenprofil",
    authorProfileDescription: "Informationen über dich, die in deiner Publikation angezeigt werden",
    profileImageLabel: "Profilbild (120×120)",
    authorNameLabel: "Autorenname",
    authorNamePlaceholder: "Dein Name",
    bioLabel: "Bio",
    bioPlaceholder: "Erzähle deinen Lesern über dich",
    imageSaved: "Bild hochgeladen",
    imageDeleted: "Bild gelöscht",
    informationSaved: "Erfolgreich gespeichert",
    social: {
      title: "soziale netzwerke",
      description: "verbinde deine sozialen profile mit deiner publikation",
      social_x: {
        label: "X (Twitter)",
        placeholder: "https://x.com/benutzername",
      },
      social_instagram: {
        label: "Instagram",
        placeholder: "https://instagram.com/benutzername",
      },
      social_tiktok: {
        label: "TikTok",
        placeholder: "https://tiktok.com/@benutzername",
      },
      social_youtube: {
        label: "YouTube",
        placeholder: "https://youtube.com/@benutzername",
      },
      social_facebook: {
        label: "Facebook",
        placeholder: "https://facebook.com/benutzername",
      },
      social_linkedin: {
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/benutzername",
      },
      social_bluesky: {
        label: "Bluesky",
        placeholder: "https://bsky.app/profile/benutzername",
      },
      social_books: {
        label: "Bücher",
        placeholder: "https://goodreads.com/user/show/benutzername",
      },
      social_website: {
        label: "Website",
        placeholder: "https://deinewebsite.com",
      },
      social_soundcloud: {
        label: "SoundCloud",
        placeholder: "https://soundcloud.com/benutzername",
      },
      social_bandcamp: {
        label: "Bandcamp",
        placeholder: "https://benutzername.bandcamp.com",
      },
    },
    subscriptions: {
      empty: "Du hast noch keine Abonnements",
      unsubscribeTitle: "Abonnement kündigen",
      unsubscribeDescription: "Du wirst keine Updates mehr von dieser Publikation erhalten.",
      unsubscribePaidDisclaimer: "Ihr aktueller bezahlter Plan wird ebenfalls gekündigt.",
      holdToUnsubscribe: "Halten zum Kündigen",
      unsubscribing: "Kündigen...",
      unsubscribed: "Abonnement gekündigt",
    },
    deleteAccount: {
      title: "Konto löschen",
      description: "Ihr Konto und alle Daten dauerhaft löschen",
      warning: "Diese Aktion kann nicht rückgängig gemacht werden. Alle Beiträge, Notizen und Abonnentendaten werden dauerhaft gelöscht.",
      button: "Konto löschen",
      confirmTitle: "Ihr Konto löschen?",
      confirmDescription: "Dies wird Ihr Konto und alle Inhalte dauerhaft löschen. Dies kann nicht rückgängig gemacht werden.",
      exportBeforeDeletePrefix: "Vor dem Löschen können Sie",
      exportBeforeDeleteSuffix: "Ihre Publikationsdaten exportieren, einschließlich aller Beiträge, Notizen und Abonnenteninformationen.",
      typeToConfirm: "Geben Sie {text} ein, um zu bestätigen",
      confirmButton: "Mein Konto löschen",
    },
  },
  post: {
    preview: "vorschau",
    previewDescription: "Sieh, wie dein Beitrag deinen Lesern angezeigt wird.",
    unarchive: "wiederherstellen",
    update: "aktualisieren",
    updated: "Beitrag erfolgreich aktualisiert",
    publish: "Weiter",
    uploadHeaderImage: "Headerbilder hochladen (16:9, 800 × 450 px)",
    title: "Titel",
    subtitle: "Untertitel",
    statusSpam: "Diese Version wurde als Spam markiert. Kontaktiere den Support, wenn du denkst, dass das ein Fehler ist.",
    statusArchived: "Diese Version ist archiviert; sie ist für andere nicht sichtbar, aber du kannst sie weiter bearbeiten.",
    statusSent: "Diese Version wurde an deine E-Mail-Abonnenten gesendet",
    statusPublished: "Diese Version ist veröffentlicht und sichtbar für deine Leser",
    statusDraft: "Dies ist ein Entwurf; du kannst ihn in der Vorschau sehen, aber er ist nicht öffentlich sichtbar",
    words: "wörter",
    savedAt: "Gespeichert um {time}",
    notes: "Notizen",
    notesDescription: "Erstelle und hefte Notizen an diesen Beitrag.",
    proofread: "Korrekturlesen",
    proofreadDescription: "Holen Sie sich Hilfe, um die Klarheit und das Engagement Ihres Beitrags zu verbessern.",
    versions: "Versionen",
    settings: "Einstellungen",
    settingsDescription: "Konfiguriere deinen Beitrag.",
    translations: "Übersetzungen",
    alsoIn: "Auch in",
    translationsDescription: "Übersetzen Sie Ihren Beitrag automatisch in andere Sprachen. Erreichen Sie mehr Leser.",
    share: "Teilen",
    shareDescription: "Holen Sie sich einen Link, um Ihren Beitrag in sozialen Medien zu teilen.",
    focusMode: "Fokusmodus",
    focusModeDescription: "Verwandeln Sie den Editor in einen ablenkungsfreien Modus, damit Sie sich auf Ihr Schreiben konzentrieren können.",
    translationOutdated: "Diese Übersetzung wurde für eine frühere Version erstellt",
    mainLanguage: "Hauptsprache",
    alternativeLanguages: "Alternative Sprachen",
    alternativeLanguagesDescription: "Aktiviere Übersetzungen für deinen Beitrag. Aktiviere Sprachen, um sie zu generieren, oder deaktiviere sie, um sie zu entfernen.",
    translationsSaved: "Übersetzungen erfolgreich gespeichert",
    translationOutdatedMessage: "Diese Übersetzung ist veraltet.",
    translationUpToDate: "Diese Übersetzung ist auf dem neuesten Stand.",
    regenerateTranslation: "Aktualisieren",
    translationOutdatedOr: "Oder",
    markTranslationAsUpdated: "Als aktuell markieren",
    updatingTranslation: "Übersetzung wird aktualisiert...",
    translationRegenerated: "Übersetzung erfolgreich aktualisiert",
    saveChanges: "Änderungen speichern",
    saveAndGenerate: "Speichern und generieren",
    saveAndDelete: "Speichern und löschen",
    publishPage: {
      update: "Aktualisieren",
      publish: "Veröffentlichen",
      delivery: "Zustellung",
      deliveryDescription: "Wie möchtest du diesen Beitrag zustellen?",
      sendViaEmail: "Per E-Mail senden",
      alreadySent: "Du hast diesen Beitrag bereits am {date} an deine E-Mail-Abonnenten gesendet",
      subscribersWillReceive: "{{Keine Abonnenten|1 Abonnent|?? Abonnenten}} werden diesen Beitrag in ihrem Posteingang erhalten.",
      sendTestToMyself: "Test an mich selbst senden",
      testEmailSent: "Test-E-Mail erfolgreich gesendet",
      testEmailError: "Fehler beim Senden der Test-E-Mail",
      audienceTitle: "Zielgruppe",
      audienceDescription: "Wählen Sie, wer diesen Beitrag sehen kann.",
    },
  },
  editor: {
    defaultPlaceholder: "Schreib etwas …",
    highlightColor: "Hervorhebungsfarbe",
    noHighlight: "Keine hervorhebung",
    grey: "Grau",
    yellow: "Gelb",
    blue: "Blau",
    green: "Grün",
    orange: "Orange",
    red: "Rot",
    bulletList: "Auflistung",
    orderedList: "Nummerierte liste",
    image: "Bild",
    code: "Code",
    quote: "Zitat",
    subscribe: "Abonnieren",
    imageTitlePlaceholder: "Bildtitel",
    imageDescriptionPlaceholder: "Beschreibung für barrierefreiheit",
    imageSizeSmall: "Klein",
    imageSizeMedium: "Mittel",
    imageSizeBig: "Volle Breite",
    turnIntoLabel: "Umwandeln in...",
    heading1: "Überschrift 1",
    heading2: "Überschrift 2",
    heading3: "Überschrift 3",
    blockquote: "Zitat",
    text: "Text",
    callout: "Hinweis",
    bold: "Fett",
    italic: "Kursiv",
    strikethrough: "Durchgestrichen",
    inlineCode: "Inline-code",
    highlight: "Hervorheben",
    link: "Link",
    horizontalRule: "Trennlinie",
    undo: "Rückgängig",
    redo: "Wiederholen",
    insertBlock: "Block einfügen",
    more: "Mehr",
    snippet: "Snippet",
    codeBlock: "Codeblock",
    math: "Math",
    embed: "Embed",
    embedDisplay: "Anzeige",
    embedDisplayEmbed: "Interaktiv",
    embedDisplayCard: "Karte",
    language: "Sprache",
  },
  notes: {
    newNote: "Neue Notiz",
    cancel: "abbrechen",
    noPinnedYet: "noch keine notizen",
    voice: "sprache",
    copyToPost: "in beitrag kopieren",
  },
  blog: {
    title: "Publikation",
    audience: "Publikum",
    forms: "Forms",
    stats: "Statistiken",
    noResultsFound: "Keine ergebnisse gefunden",
    noPostsYet: "Du hast noch keine Beiträge erstellt.",
    filter: {
      spam: "Spam",
      archived: "Archiviert",
      sent: "Gesendet",
      published: "Veröffentlicht",
      draft: "Entwurf",
    },
    blogName: "Publikation-Name",
    blogNameDescription: "Lege den Namen deiner Publikation fest: er wird verwendet, um deine Publikation zu identifizieren und die URL zu erstellen",
    atLeast3Characters: "3-15 Zeichen: Buchstaben, Zahlen oder Bindestriche.",
    nameAvailable: "Dieser Name ist verfügbar.",
    nameInvalid: "Dieser Name ist ungültig.",
    nameUnavailable: "Dieser Name ist bereits vergeben.",
    nameNaughty: "Dieser Name ist nicht erlaubt.",
    nameChangeCooldown: "Du kannst deinen Namen in {days} {{Tag|Tagen}} wieder ändern.",
    nameChangeWarning: "Nach der Änderung kannst du deinen Namen 30 Tage lang nicht ändern. Deine alte Adresse leitet auf die neue weiter.",
    checkingNameAvailability: "Verfügbarkeit des Namens wird geprüft...",
    language: "Sprache",
    languageDescription: "Wähle die Sprache deiner Veröffentlichung",
    additionalLanguages: "Zusätzliche Sprachen",
    additionalLanguagesDescription: "Wähle aus, welche Sprachen für Übersetzungen in deiner Veröffentlichung verfügbar sind. Alle Sprachen sind standardmäßig aktiviert.",
    titleAndDescription: "Titel und Beschreibung",
    titleAndDescriptionDescription: "Lege Titel und Beschreibung deines Bereichs fest",
    yourBlogTitle: "titel-deiner-publikation",
    description: "Beschreibung",
    yourBlogDescription: "beschreibung-deiner-publikation",
    branding: "Branding",
    brandingDescription: "Füge deiner Publikation und deinen E-Mails dein eigenes Branding hinzu",
    logo: "Logo",
    logoDescription: "Wird oben in der Publikation und in E-Mails angezeigt. Empfohlene Größe: 200 × 200 Pixel.",
    uploadLogo: "Logo hochladen",
    wordmark: "Wortmarke",
    wordmarkDescription: "Wird anstelle des Publikation-Namens oben in der Publikation angezeigt. Empfohlene Größe: 1000 × 200 Pixel.",
    uploadWordmark: "Wortmarke hochladen",
    subscribeImage: "Abonnieren-Bild",
    subscribeImageDescription: "Wird im Abonnieren-Formular angezeigt. Empfohlene Größe: 600 × 600 Pixel.",
    uploadSubscribeImage: "Bild hochladen",
    favicon: "Favicon",
    faviconDescription: "Wird als Browser-Tab-Symbol für deine Publikation angezeigt. Empfohlene Größe: 128 × 128 Pixel.",
    uploadFavicon: "Favicon hochladen",
    header: "Header",
    headerDescription: "Füge jeder deiner Veröffentlichungen einen individuellen Header hinzu",
    footer: "Footer",
    footerDescription: "Füge jeder deiner Veröffentlichungen einen individuellen Footer hinzu",
    welcomeEmail: {
      title: "Willkommens-E-Mail",
      description: "Passe die Willkommens-E-Mail für neue Abonnenten an",
      subjectLabel: "E-Mail-Betreff",
      subjectContent: "E-Mail-Inhalt",
    },
    export: {
      title: "Exportieren",
      description: "Exportiere alle Abonnenten und Inhalte deiner Publikation",
      generateExport: "Export generieren",
      generating: "Wird generiert...",
      exportedAt: "Exportiert am",
      subscribers: "Abonnenten",
      posts: "Beiträge",
      download: "Herunterladen",
      processing: "Wird verarbeitet...",
      error: "Fehler",
      noExports: "Noch keine Exporte",
    },
    subscribersPage: {
      email: "E-Mail",
      subscribedAt: "Abonniert am",
      tier: "Stufe",
      form: "Form",
      allForms: "All forms",
      direct: "Direct",
      free: "Kostenlos",
      allTiers: "Alle Stufen",
      noSubscribersFound: "Keine Abonnenten gefunden",
      noSubscribersYet: "Noch keine Abonnenten",
      publication: "Publikation",
      followBack: "Abonnieren",
      following: "Abonniert",
    },
    formsPage: {
      editorTitle: "Create form",
      editEditorTitle: "Edit form",
      createForm: "Create form",
      editForm: "Edit form",
      name: "Name",
      audience: "Empfänger",
      audienceDescription: "Wählen Sie, wer die E-Mail mit den Dateien erhalten kann. Bei eingeschränktem Zugriff müssen Nutzer zuerst abonnieren oder upgraden.",
      downloads: "Files",
      subscribers: "Subscribers",
      size: "Size",
      actions: "Actions",
      everyone: "Alle",
      everyoneDescription: "Jeder kann das Formular absenden und die E-Mail mit den Dateien erhalten.",
      subscribersOnly: "Nur Abonnenten",
      subscribersOnlyDescription: "Nur Abonnenten können die E-Mail mit den Dateien erhalten.",
      content: "Description",
      email: "Email",
      emailSubject: "Email subject",
      emailBody: "Email body",
      formSectionTitle: "Create form",
      formSectionDescription: "Set up your form and file delivery",
      filesSectionTitle: "Files",
      filesSectionDescription: "Upload the files readers will receive",
      emailSectionDescription: "Customize the delivery email sent after submit",
      defaultDescription: "Abonniere, um diese Dateien per E-Mail zu erhalten.",
      defaultEmailSubject: "Deine Dateien sind bereit",
      defaultEmailSubjectWithName: "{publicationName}: Deine Dateien sind bereit",
      defaultEmailGreeting: "Hallo,",
      defaultEmailIntro: "danke fuers Abonnieren. Deine Download-Links findest du unten.",
      defaultEmailOutro: "Wenn du Fragen hast, antworte einfach auf diese E-Mail.",
      defaultEmailSignature: "- Das Team",
      defaultEmailSignatureWithName: "- {publicationName}",
      dropFiles: "Drop the files",
      formCreated: "Form created",
      formUpdated: "Form updated",
      formDeleted: "Form deleted",
      blankSlate: {
        title: "Capture, nurture, convert",
        growReadership: "Grow your readership",
        growReadershipDescription: "Capture more readers by offering exclusive content behind a subscription form.",
        convertReaders: "Convert readers into paid subscribers",
        convertReadersDescription: "Offer useful downloads and resources that turn readers into subscribers.",
        goBeyondText: "Go beyond text",
        goBeyondTextDescription: "Give your audience more by supplementing your written content with downloadable media and resources.",
      },
    },
    reactionEmojis: "Reaktions-Emojis",
    reactionEmojisDescription: "Wähle, welche Emojis Leser verwenden können, um auf deine Beiträge zu reagieren",
    addEmoji: "Emoji hinzufügen",
    interval: "zeitraum",
    referrer: "referrer",
    visitors: "besucher",
    views: "aufrufe",
    country: "land",
    channel: "kanal",
  },
  categories: {
    culture: "Kultur",
    technology: "Technologie",
    business: "Business",
    usPolitics: "US-Politik",
    finance: "Finanzen",
    food: "Essen",
    podcast: "Podcast",
    sports: "Sport",
    art: "Kunst",
    worldPolitics: "Weltpolitik",
    healthPolitics: "Gesundheitspolitik",
    news: "Nachrichten",
    fashionBeauty: "Mode & Beauty",
    music: "Musik",
    faith: "Glaube",
    climate: "Klima",
    science: "Wissenschaft",
    literature: "Literatur",
    fiction: "Belletristik",
    health: "Gesundheit",
    design: "Design",
    travel: "Reisen",
    parenting: "Erziehung",
    philosophy: "Philosophie",
    comics: "Comics",
    international: "International",
    crypto: "Krypto",
    history: "Geschichte",
    humor: "Humor",
    education: "Bildung",
    filmTv: "Film & TV",
  },
  readerOnboarding: {
    subscribedTo: "Du hast \"{publicationTitle}\" abonniert",
    selectInterests: "Welche Themen interessieren dich?",
    selectInterestsDescription: "Wähle die Kategorien aus, über die du lesen möchtest. Das hilft uns, dir Publikationen zu empfehlen, die dir gefallen werden.",
    continue: "Weiter",
    skip: "Erstmal überspringen",
  },
  onboarding: {
    buildAudience: "Baue deine eigene Leserschaft auf — zu deinen Bedingungen",
    ownYourContent: "Auf Fika besitzt du, was du schreibst, und die Gemeinschaft, die sich darum bildet.",
    nameYourBlog: "Beginne damit, deiner Publikation einen Namen zu geben; dein eigener Ort im offenen, wilden Web.",
    yourBlogNamePlaceholder: "dein-publikation-name",
    createBlog: "Publikation erstellen",
    continue: "Weiter",
    updateBlog: "Publikation aktualisieren",
    onboardingRabbitAlt: "Onboarding-Hase",
    chooseLanguage: {
      title: "In welchen Sprachen wirst du schreiben?",
      description: "Wähle die Sprache deiner Publikation und aktiviere weitere für Übersetzungen.",
    },
    createFirstPost: {
      title: "Veröffentliche deinen ersten Artikel",
      description: "Beginne mit dem Schreiben deines ersten Artikels und starte mit Fika. Teile deine Gedanken mit der Welt!",
      descriptionWithDraft: "Fahre fort mit dem Schreiben deines ersten Artikels und starte mit Fika. Teile deine Gedanken mit der Welt!",
      ignore: "Ignorieren",
      createPost: "Artikel erstellen",
      continueWriting: "Weiter schreiben",
    },
    createSecondPost: {
      title: "Veröffentliche deinen zweiten Artikel",
      description: "Schreibe einen weiteren Beitrag und teile ihn mit deinen Lesern.",
      descriptionWithDraft: "Schreibe an deinem zweiten Beitrag weiter.",
      ignore: "Ignorieren",
      createPost: "Artikel erstellen",
      continueWriting: "Weiter schreiben",
      creating: "Erstelle...",
    },
    fillProfile: {
      title: "Vervollständige dein Profil",
      description: "Füge deinen Namen, deine Biografie und dein Profilbild hinzu, um deine Fika-Präsenz zu personalisieren.",
      ignore: "Ignorieren",
      editProfile: "Profil bearbeiten",
    },
    setLanguage: {
      title: "Lege die Sprache deiner Publikation fest",
      description: "Wähle die Hauptsprache für deine Publikation. Dies hilft Lesern, deine Inhalte zu finden.",
      ignore: "Ignorieren",
      chooseLanguage: "Sprache wählen",
    },
    fillPublication: {
      title: "Füge Publikationsdetails hinzu",
      description: "Gib deiner Publikation einen Titel, eine Beschreibung und ein Logo, damit sie auffällt.",
      ignore: "Ignorieren",
      editDetails: "Details bearbeiten",
    },
    translateArticles: {
      title: "Übersetze \"{title}\"",
      description: "Füge Übersetzungen zu deinen Artikeln hinzu, um Leser in verschiedenen Sprachen zu erreichen.",
      ignore: "Ignorieren",
      translateLatestArticle: "Artikel übersetzen",
    },
  },
  postSettings: {
    title: "einstellungen",
    customPagesTitle: "benutzerdefinierte seiten",
    customPagesDescription: "lasse diesen beitrag in der navigationsleiste erscheinen",
    customPageLabel: "benutzerdefinierte seite",
    seoMetadataTitle: "seo metadata",
    seoMetadataDescription: "der titel und die beschreibung des beitrags, wie er in suchergebnissen angezeigt wird",
    seoMetadataSaved: "metadata gespeichert",
    metaTitleLabel: "meta-titel",
    metaTitlePlaceholder: "titel deiner publikation",
    metaDescriptionLabel: "meta-beschreibung",
    metaDescriptionPlaceholder: "beschreibung deiner publikation",
    canonicalTitle: "canonical",
    canonicalDescription: "wenn die originalquelle des beitrags eine andere website ist, kannst du hier die canonical-url angeben, um probleme mit doppeltem inhalt zu vermeiden.",
    canonicalUrlLabel: "canonical-url",
    canonicalUrlSaved: "canonical-url gespeichert",
    pinSaved: "benutzerdefinierte seite gespeichert",
    tagsTitle: "tags",
    tagsDescription: "füge deinem beitrag tags hinzu",
    audienceSaved: "zielgruppeneinstellungen gespeichert",
    audienceTitle: "Zielgruppe",
    audienceDescription: "Wählen Sie, wer auf diesen Beitrag zugreifen kann.",
    audienceEveryoneLabel: "Alle",
    audienceEveryoneDescription: "Jeder kann sehen, einschließlich Besucher, die nicht abonniert sind.",
    audienceAllSubscribersLabel: "Nur Abonnenten",
    audienceAllSubscribersDescription: "Jeder Abonnent kann diesen Beitrag sehen.",
    commentsEnabledTitle: "Comments",
    commentsEnabledDescription: "Allow readers to comment on this post.",
    publishedAtTitle: "veröffentlichungsdatum",
    publishedAtDescription: "das Datum der Erstveröffentlichung dieses Beitrags ändern.",
    publishedAtLabel: "veröffentlichungsdatum",
    publishedAtSaved: "veröffentlichungsdatum gespeichert",
    archivePostTitle: "beitrag archivieren",
    unarchivePostTitle: "beitrag wiederherstellen",
    archivePostDescription: "verstecke diesen beitrag in deiner publikation.",
    unarchivePostDescription: "zeige diesen beitrag in deiner publikation.",
    archivePostSaved: "beitrag archiviert",
    deletePostTitle: "beitrag löschen",
    deletePostDescription: "entferne diesen beitrag vollständig.",
    deletePostWarning: "diese aktion kann nicht rückgängig gemacht werden.",
    deletePostSaved: "beitrag gelöscht",
    holdToDelete: "Halten zum Löschen",
    deleting: "Löschen...",
    deleted: "Gelöscht!",
  },
  postShare: {
    preview: "Vorschau",
    previewDescription: "So wird dein beitrag in sozialen medien und suchergebnissen angezeigt",
    articleLink: "Artikel-link",
    copyLink: "Link kopieren",
    linkCopied: "Link in die zwischenablage kopiert",
    metaTitleLabel: "Meta-titel",
    metaTitlePlaceholder: "Dein publikationstitel",
    metaDescriptionLabel: "Meta-beschreibung",
    metaDescriptionPlaceholder: "Deine publikationsbeschreibung",
    shareOnSocialMedia: "In sozialen medien teilen",
    shareOn: "Auf {network} teilen",
    publishedCongrats: "Dein Beitrag ist online!",
    shareNow: "Teile ihn jetzt mit der Welt! 🚀",
    backToPost: "Zurück zum Beitrag",
    setCoverImage: "Als Titelbild setzen",
    resetCoverImage: "Auf Original zurücksetzen",
    coverImageUpdated: "Titelbild aktualisiert",
    socialImages: "Social-Media-Bilder",
    socialImagesDescription: "Bilder für soziale Medien herunterladen",
    download: "Herunterladen",
  },
  postVersions: {
    title: "versionen",
    loading: "versionen werden geladen...",
    failed: "versionen konnten nicht geladen werden",
    failedVersion: "version konnte nicht geladen werden",
    restored: "version erfolgreich wiederhergestellt",
    restore: "wiederherstellen",
    language: "sprache",
    pills: {
      current: "aktuell",
      archived: "archiviert",
      sent: "gesendet",
      published: "veröffentlicht",
    },
  },
  subscribe: {
    defaultText: "Abonniere {userName}, um Updates direkt in deinen Posteingang zu erhalten",
    button: "Abonnieren",
    emailPlaceholder: "Gib deine E-Mail ein",
  },
  snippet: {
    placeholder: "Beschreibe, was du erstellen möchtest...",
    placeholderUpdate: "Beschreibe, was du ändern möchtest...",
    buttonCreate: "erstellen",
    buttonUpdate: "aktualisieren",
    buttonCreating: "Erstelle...",
    buttonUpdating: "Aktualisiere...",
  },
  auth: {
    signInOrSignUp: "Anmelden",
    registrationsClosed: "Neuregistrierungen sind geschlossen. Bestehende Nutzer können sich weiterhin anmelden.",
    accountSuspendedTitle: "Konto gesperrt",
    accountSuspendedMessage: "Dein Konto wurde gesperrt. Schreib uns an humans@fika.bar, wenn du denkst, dass dies ein Fehler ist.",
    continueWithGoogle: "Mit Google fortfahren",
    continueWithLinkedIn: "Mit LinkedIn fortfahren",
    signingIn: "Anmelden...",
    or: "oder",
    enterYourEmail: "Gib deine E-Mail ein",
    failedToSend: "Der Verifizierungscode konnte nicht gesendet werden. Bitte versuche es erneut.",
    sending: "Senden...",
    enterCode: "Bitte gib den 6-stelligen Code ein, der gesendet wurde an",
    verifying: "Überprüfen...",
    invalidCode: "Ungültiger oder abgelaufener Code. Bitte versuche es erneut.",
    useDifferentEmail: "Andere E-Mail verwenden",
    subscribeTo: "Abonnieren von",
    thisBlog: "dieser Publikation",
    getUpdates: "Abonniere, um Aktualisierungen direkt in deinen Posteingang zu erhalten",
    subscribe: "Abonnieren",
    somethingWentWrong: "Etwas ist schiefgelaufen",
    disclaimerPrefix: "Durch das Absenden akzeptierst du die",
    disclaimerPrivacy: "Datenschutzrichtlinie",
    disclaimerAnd: "und stimmst den",
    disclaimerTerms: "Nutzungsbedingungen zu",
    tierSelectionTitleSubscribed: "Du hast {publicationName} abonniert!",
    tierSelectionTitleSubscribedNoName: "Du bist abonniert!",
    subscriptionError: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
    verifyEmailSuccess: "E-Mail erfolgreich verifiziert!",
    emailNotVerified: "Bitte verifiziere deine E-Mail-Adresse, um diese Aktion durchzuführen",
    subscriptionConfirmationDescriptionBeforeAuthor: "Du erhältst Updates von ",
    subscriptionConfirmationDescriptionAfterAuthor: " direkt in deinem Posteingang.",
    subscriptionConfirmationDescriptionNoAuthor: "Du erhältst Updates direkt in deinem Posteingang.",
  },
  unsubscribe: {
    writerSuccess: "du hast dich abgemeldet",
    writerSuccessDescription: "du erhältst keine e-mails mehr von diesem autor. du kannst dich jederzeit über sein profil wieder anmelden.",
    marketingSuccess: "du hast dich abgemeldet",
    marketingSuccessDescription: "du erhältst keine marketing-e-mails mehr von fika. du kannst deine einstellungen in den einstellungen verwalten.",
    weeklyFeaturedSuccess: "du wurdest abgemeldet",
    weeklyFeaturedSuccessDescription: "du erhältst die wöchentliche E-Mail zu vorgestellten Beiträgen nicht mehr. du kannst sie in den Einstellungen wieder aktivieren.",
    error: "etwas ist schief gelaufen",
    invalidToken: "dieser abmeldelink ist ungültig oder abgelaufen. bitte versuche es erneut mit einer aktuellen e-mail.",
    missingToken: "es wurde kein abmeldetoken bereitgestellt. bitte verwende den link aus deiner e-mail.",
    genericError: "ein unerwarteter fehler ist aufgetreten. bitte versuche es später erneut.",
    invalidRequest: "ungültige anfrage",
    invalidRequestDescription: "diese seite erfordert einen gültigen abmeldelink aus einer e-mail.",
    signInToManage: "anmelden, um abonnements zu verwalten",
  },
  subscriptions: {
    title: "Abonnements",
    manage: "Verwalten",
    manageTitle: "Abonnements verwalten",
    viewOriginal: "Original anzeigen",
    noPublishedPostsYet: "Noch keine veröffentlichten Beiträge",
    previousPost: "Vorheriger Beitrag",
    nextPost: "Nächster Beitrag",
    following: "Folge ich",
    subscribed: "Abonniert",
    renewsAt: "Verlängert sich am {date}",
    upgradePlan: "Upgrade",
    tierChangeError: "Plan konnte nicht geändert werden",
    manageBilling: "Abrechnung & Rechnungen",
    subscribersOnly: "Nur Abonnenten",
    tableOfContents: "Inhaltsverzeichnis",
  },
  discover: {
    readMore: "Weiterlesen",
    upgradeToRead: "Upgrade zum Lesen",
    humanContent: "Mensch",
    humanContentTitle: "Menschliche Inhalte",
    humanContentDescription: "Fika ist sehr zuversichtlich, dass dieser Inhalt von einem Menschen mit wenig oder keiner KI-Unterstützung verfasst wurde.",
  },
  privatePost: {
    subscribeCta: "Abonnieren",
    upgradeCta: "Upgraden zum Freischalten",
  },
  applicationLayout: {
    loadingPhrases: {
      synchronizing: "synchronisiere...",
      hadronCollider: "baue den hadronenbeschleuniger zusammen...",
      reticulatingSplines: "retikuliere splines...",
      wakingHamsters: "wecke die hamster auf...",
      consultingOracle: "befrage das orakel...",
      aligningStars: "richte die sterne aus...",
      fluxCapacitor: "kalibriere den fluxkompensator...",
      traveling88mph: "reise mit 88 mph...",
      whiteRabbit: "folge dem weißen kaninchen...",
      reversingPolarity: "kehre die polarität um...",
      enhancingImage: "verbessere das bild...",
      enteringMatrix: "betrete die matrix...",
      findingRing: "finde den einen ring...",
      dividingByZero: "teile durch null...",
    },
  },
  proofreader: {
    title: "Korrektur",
    editorTitle: "Editor",
    editorDescription: "Überprüfe deinen beitrag, um klarheit und wirkung zu verbessern.",
    principles: {
      grammar: {
        label: "Grammatik",
        description: "Rechtschreib- und grammatikfehler korrigieren.",
        action: "Grammatik korrigieren",
      },
      tighten: {
        label: "Straffen",
        description: "Den text prägnanter machen.",
        action: "Text straffen",
      },
      refine: {
        label: "Verfeinern",
        description: "Klarheit, fluss und wirkung des textes verbessern.",
        action: "Text verfeinern",
      },
    },
    noFixesFound: "Keine korrekturen gefunden",
    countFixes: "{{Keine korrekturen|1 korrektur|?? korrekturen}}",
    countFixesFound: "{{Keine korrekturen gefunden|1 korrektur gefunden|?? korrekturen gefunden}}",
    scanning: "Wird gescannt...",
    percentComplete: "{percent}% abgeschlossen",
    discard: "Verwerfen",
    acceptAll: "Alle akzeptieren",
    lenses: {
      title: "Linsen",
      description: "Visualisiere die satzstruktur, um rhythmus und lesbarkeit zu verbessern.",
      off: "aus",
      difficulty: "schwierigkeit",
      difficultyDescription: "Zeigt, wie schwierig deine sätze sind. Schwierigere sätze erscheinen in wärmeren farben. Zur Verbesserung verwende kürzere sätze und einfachere wörter.",
      variation: "variation",
      variationDescription: "Zeigt, wie unterschiedlich deine satzlängen sind. Monotone sätze erscheinen in wärmeren farben. Zur Verbesserung verwende eine mischung aus kurzen und langen sätzen.",
      legend: {
        difficulty: {
          orange: "Sehr schwer zu lesen",
          amber: "Schwer zu lesen",
        },
        variation: {
          orange: "Sehr monoton",
          amber: "Monoton",
        },
      },
      tips: {
        veryHard: "Dieser satz ist sehr schwer zu lesen. Versuche, ihn in kürzere sätze aufzuteilen oder einfachere wörter zu verwenden.",
        hard: "Dieser satz ist schwer zu lesen. Erwäge, ihn zu vereinfachen.",
        veryMonotonous: "Deine sätze sind sehr ähnlich in der länge. Variiere den rhythmus durch mischung kurzer und langer sätze.",
        monotonous: "Dieser satz ist ähnlich wie die benachbarten. Versuche, die länge zu variieren für besseren fluss.",
      },
    },
  },
  inbox: {
    title: "Posteingang",
    message: "Nachricht",
    empty: "Noch keine Briefe oder Kommentare",
    emptyLetters: "Noch keine Briefe",
    emptyComments: "Noch keine Kommentare",
    emptyDescription: "Nutze diesen Bereich, um Briefe von Lesern und Gespräche mit Autoren zu sehen, die du abonniert hast.",
    unread: "Ungelesen",
    read: "Gelesen",
    inReplyTo: "Als Antwort auf:",
    writeMessage: "Nachricht schreiben...",
    submit: "Senden",
    publishing: "Wird veröffentlicht...",
    you: "Du",
    tabAll: "Alle",
    tabEmails: "Briefe",
    tabComments: "Kommentare",
    typePrivateEmail: "Privater Brief",
    typePublicComment: "Öffentlicher Kommentar",
    andMore: "und {count} mehr",
  },
  comments: {
    title: "Kommentare",
    writeComment: "Schreibe einen Kommentar...",
    submit: "Kommentar veröffentlichen",
    publishing: "Wird veröffentlicht...",
    reply: "Antworten",
    delete: "Löschen",
    deleting: "Wird gelöscht...",
    deleteConfirm: "Diesen Kommentar löschen?",
    empty: "Noch keine Kommentare. Sei der Erste, der kommentiert!",
    anonymousParticipant: "Ein Abonnent",
    flaggedMessage: "Deine Nachricht wurde als Spam oder unangemessener Inhalt eingestuft und konnte nicht gesendet werden.",
    author: "Autor",
    privateVisibility: "Nur für dich und die Teilnehmer sichtbar",
    sendAsPrivate: "Als privaten Brief senden",
    translateTo: "Übersetzen nach {locale}",
    showOriginal: "Original anzeigen ({locale})",
  },
} satisfies Translation

export default de
